import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Carrot, ChefHat, Martini, BookText, BedDouble, CalendarCheck,
  AlertTriangle, Trash2, BarChart3, Sparkles, UtensilsCrossed, FileSpreadsheet, FileText,
  Settings, CreditCard, Handshake, LogOut, ShieldCheck, Lock, Menu, X, Users, Building2,
} from "lucide-react";
import BrandMark from "./BrandMark";
import LanguageToggle from "./LanguageToggle";
import OutletSwitcher from "./OutletSwitcher";
import OutletBreadcrumb from "./OutletBreadcrumb";
import DemoTourButton from "./DemoTourButton";
import LegalFooter from "./LegalFooter";
import { useAuth } from "../contexts/AuthContext";
import { hasModule, canSeeNav } from "./RouteGuards";

function NavItem({ to, icon: Icon, label, soon = false, locked = false, end = false, testId, subtitle }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      title={subtitle || undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors group ${
          isActive
            ? "text-brand bg-brand/10"
            : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
        } ${soon ? "cursor-not-allowed opacity-60" : ""}`
      }
      onClick={(e) => { if (soon) e.preventDefault(); }}
    >
      <Icon size={16} strokeWidth={1.6} />
      <span className="flex-1 flex flex-col">
        <span>{label}</span>
        {subtitle && (
          <span className="text-[9px] text-ink-muted font-normal leading-none mt-0.5" data-testid={`${testId}-subtitle`}>
            {subtitle}
          </span>
        )}
      </span>
      {soon && (
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-ink-muted border border-white/10 flex items-center gap-1">
          <Lock size={9} /> bald
        </span>
      )}
      {locked && !soon && (
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-warn/10 text-warn border border-warn/20 flex items-center gap-1" data-testid={`${testId}-locked`}>
          <Lock size={9} /> Plan
        </span>
      )}
    </NavLink>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-3 mt-5 mb-2 text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
      {children}
    </div>
  );
}

export default function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "super_admin";
  const canKitchen = hasModule(user, "kitchen");
  const canBar = hasModule(user, "bar");
  const canAI = hasModule(user, "ai");

  // Phase 34 — role-based sidebar visibility. Owners/super-admin see everything;
  // team members (gm, fnb_manager, kitchen_chef, bar_manager, service) are
  // restricted by team_role regardless of inherited plan modules.
  const see = (navKey) => canSeeNav(user, navKey);

  const sidebar = (
    <nav className="flex flex-col h-full bg-bg-void border-r border-white/10 w-64" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-white/5 flex items-center justify-between">
        <Link to="/dashboard"><BrandMark /></Link>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-ink-secondary hover:text-ink-primary"
          data-testid="sidebar-close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4">
        <OutletSwitcher />
        <SectionLabel>Workspace</SectionLabel>
        <div className="flex flex-col gap-0.5">
          {see("dashboard") && <NavItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} end testId="nav-dashboard" />}
          {see("ingredients") && <NavItem to="/ingredients" icon={Carrot} label={t("nav.ingredients")} testId="nav-ingredients" />}
          {see("recipes") && <NavItem to="/recipes" icon={ChefHat} label={t("nav.recipes")} locked={!canKitchen} testId="nav-recipes" />}
          {see("cocktails") && <NavItem to="/cocktails" icon={Martini} label={t("nav.cocktails")} locked={!canBar} testId="nav-cocktails" />}
          {see("menus") && <NavItem to="/menus" icon={BookText} label={t("nav.menus")} locked={!hasModule(user, "menus")} testId="nav-menus" />}
          {see("event-menus") && <NavItem to="/event-menus" icon={CalendarCheck} label={t("nav.event_menus")} locked={!hasModule(user, "menus")} testId="nav-event-menus" />}
          {see("hotel") && <NavItem to="/hotel" icon={BedDouble} label={t("nav.hotel")} locked={!hasModule(user, "hotel")} testId="nav-hotel" />}
        </div>

        <SectionLabel>Operations</SectionLabel>
        <div className="flex flex-col gap-0.5">
          {see("allergens") && <NavItem to="/allergens" icon={AlertTriangle} label={t("nav.allergens")} locked={!hasModule(user, "allergens")} testId="nav-allergens" />}
          {see("waste") && <NavItem to="/waste" icon={Trash2} label={t("nav.waste")} locked={!hasModule(user, "waste")} testId="nav-waste" />}
          {see("engineering") && <NavItem to="/engineering" icon={BarChart3} label={t("nav.engineering")} locked={!hasModule(user, "engineering")} testId="nav-engineering" />}
          {see("ai") && (
            <NavItem
              to="/ai"
              icon={Sparkles}
              label={t("nav.ai")}
              locked={!canAI}
              testId="nav-ai"
            />
          )}
          {see("plating") && (
            <NavItem
              to="/plating"
              icon={UtensilsCrossed}
              label="Plating Intelligence"
              locked={!hasModule(user, "plating")}
              testId="nav-plating"
            />
          )}
          {see("drink") && (
            <NavItem
              to="/drink"
              icon={Martini}
              label="Drink Intelligence"
              locked={!hasModule(user, "drink")}
              testId="nav-drink"
            />
          )}
          {see("reports") && <NavItem to="/reports" icon={FileSpreadsheet} label={t("nav.reports")} locked={!hasModule(user, "reports")} testId="nav-reports" />}
          {see("invoices") && <NavItem to="/invoices" icon={FileText} label={t("nav.invoices")} locked={!hasModule(user, "invoices")} testId="nav-invoices" />}
        </div>

        <SectionLabel>Account</SectionLabel>
        <div className="flex flex-col gap-0.5">
          {see("settings") && <NavItem to="/settings" icon={Settings} label={t("nav.settings")} testId="nav-settings" />}
          {see("billing") && !user?.team_owner_id && (
            <NavItem to="/billing" icon={CreditCard} label={t("nav.billing")} testId="nav-billing" />
          )}
          {see("outlets") && user?.plan === "premium" && (
            <NavItem to="/outlets" icon={Building2} label="Outlets" testId="nav-outlets" />
          )}
          {see("unified-dashboard") && user?.plan === "premium" && !user?.team_owner_id && (
            <NavItem to="/outlets/dashboard" icon={LayoutDashboard} label="Unified Dashboard" testId="nav-unified-dashboard" />
          )}
          {isAdmin && (
            <NavItem to="/affiliate" icon={Handshake} label={t("nav.partner")} testId="nav-partner" />
          )}
        </div>

        {isAdmin && (
          <>
            <SectionLabel>{t("nav.admin")}</SectionLabel>
            <div className="flex flex-col gap-0.5">
              <NavItem to="/admin" icon={ShieldCheck} label={t("nav.admin")} end testId="nav-admin-overview" />
              <NavItem to="/admin/users" icon={Users} label={t("nav.admin_users")} testId="nav-admin-users" />
            </div>
          </>
        )}
      </div>

      {/* User card */}
      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-bg-surface border border-white/10">
          <div className="w-8 h-8 rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center text-brand text-xs font-semibold">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-ink-primary truncate" data-testid="user-name">
              {user?.name || user?.email || "—"}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted" data-testid="user-role">
              {isAdmin ? "Super Admin" : "Gastronom"}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            className="text-ink-secondary hover:text-err transition-colors"
            data-testid="logout-button"
            aria-label="Logout"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-bg-void text-ink-primary flex app-grain" data-testid="app-shell">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex sticky top-0 h-screen z-30">{sidebar}</div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden slide-in">{sidebar}</div>
        </>
      )}

      {/* Main */}
      <div className="relative z-10 flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-bg-void/85 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-5 md:px-8" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-ink-secondary hover:text-ink-primary"
              data-testid="sidebar-open"
              aria-label="Menu"
            >
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-2 text-xs text-ink-secondary font-mono">
              <span className="hidden md:inline">studioops.food</span>
              <span className="text-ink-muted">/</span>
              <span className="text-ink-primary">{routeLabel(location.pathname, t)}</span>
            </div>
            <OutletBreadcrumb />
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
          </div>
        </header>

        <main className="flex-1 p-5 md:p-8 fade-in" data-testid="main-content">
          <Outlet />
        </main>
        <LegalFooter />
      </div>
      <DemoTourButton />
    </div>
  );
}

function routeLabel(path, t) {
  if (path.startsWith("/admin/users")) return t("nav.admin_users");
  if (path.startsWith("/admin")) return t("nav.admin");
  if (path.startsWith("/ingredients")) return t("nav.ingredients");
  if (path.startsWith("/recipes")) return t("nav.recipes");
  if (path.startsWith("/cocktails")) return t("nav.cocktails");
  if (path.startsWith("/ai")) return t("nav.ai");
  if (path.startsWith("/allergens")) return t("nav.allergens");
  if (path.startsWith("/menus")) return t("nav.menus");
  if (path.startsWith("/waste")) return t("nav.waste");
  if (path.startsWith("/engineering")) return t("nav.engineering");
  if (path.startsWith("/hotel")) return t("nav.hotel");
  if (path.startsWith("/reports")) return t("nav.reports");
  if (path.startsWith("/invoices")) return t("nav.invoices");
  if (path.startsWith("/settings")) return t("nav.settings");
  if (path.startsWith("/billing")) return t("nav.billing");
  if (path.startsWith("/dashboard")) return t("nav.dashboard");
  return path.slice(1);
}
